/**
 * Normalize, escape, and resolve background image URLs for landing page tiles and slideshows.
 * Mobile Safari is unreliable with dozens of concurrent CSS background-image requests on first paint.
 */

/**
 * @param {string} url
 * @param {{ source?: string, externalId?: string } | null} [meta]
 * @returns {string}
 */
export function normalizeBackgroundImageUrl(url, meta = null) {
    if (!url || typeof url !== 'string') return '';
    const s = url.trim();
    if (s.startsWith('/api/backgrounds/serve-facebook/')) return s;
    if (
        meta?.source === 'facebook' &&
        meta?.externalId &&
        /^https?:\/\//i.test(s) &&
        /fbcdn\.net|facebook\.com/i.test(s)
    ) {
        return `/api/backgrounds/serve-facebook/${encodeURIComponent(String(meta.externalId))}`;
    }
    if (s.startsWith('http') || s.startsWith('//') || s.startsWith('/')) return s;
    const withoutLeadingSlash = s.startsWith('/') ? s.slice(1) : s;
    return `/uploads/${withoutLeadingSlash}`;
}

/**
 * @param {string} url
 * @returns {string}
 */
export function escapeUrlForCss(url) {
    if (!url || typeof url !== 'string') return '';
    return url.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * @param {string} url
 * @returns {string}
 */
export function toAbsoluteImageUrl(url) {
    const normalized = normalizeBackgroundImageUrl(url);
    if (!normalized) return '';
    if (/^https?:\/\//i.test(normalized) || normalized.startsWith('//')) {
        return normalized;
    }
    try {
        return new URL(normalized, window.location.origin).href;
    } catch {
        return normalized;
    }
}

/**
 * Wait until an element has non-zero layout dimensions (mobile first-paint can report 0×0).
 * @param {HTMLElement} el
 * @param {number} [timeoutMs]
 * @returns {Promise<void>}
 */
export function waitForContainerLayout(el, timeoutMs = 4000) {
    if (!el) return Promise.resolve();

    const isReady = () => el.offsetWidth > 0 && el.offsetHeight > 0;

    return new Promise((resolve) => {
        if (isReady()) {
            resolve();
            return;
        }

        const deadline = Date.now() + timeoutMs;
        let ro = null;

        const finish = () => {
            if (ro) ro.disconnect();
            resolve();
        };

        const check = () => {
            if (isReady() || Date.now() >= deadline) {
                finish();
                return;
            }
            requestAnimationFrame(check);
        };

        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => {
                if (isReady()) finish();
            });
            ro.observe(el);
        }

        requestAnimationFrame(check);
    });
}

/**
 * @param {HTMLImageElement} img
 * @param {number} [maxAttempts]
 */
export function retryImageLoad(img, maxAttempts = 4) {
    if (!img?.dataset?.src) return;
    const attempt = Number(img.dataset.retryAttempt || '0');
    if (attempt >= maxAttempts) return;
    img.dataset.retryAttempt = String(attempt + 1);
    const base = img.dataset.src.split('?')[0];
    window.setTimeout(() => {
        img.src = `${base}?retry=${attempt + 1}`;
    }, 400 * (attempt + 1));
}

/** Mobile Safari struggles with 30+ simultaneous image requests on first paint. */
export const FOOD_GALLERY_LOAD_BATCH = 5;
export const FOOD_GALLERY_BATCH_DELAY_MS = 100;

/**
 * Load deferred mosaic tile images in small batches.
 * @param {HTMLElement} container
 * @returns {Promise<void>}
 */
export async function loadDeferredTileImages(container) {
    if (!container) return;

    const pending = [...container.querySelectorAll('img[data-src]:not([data-loaded])')];
    if (pending.length === 0) return;

    container._foodGalleryLoading = true;

    try {
        for (let i = 0; i < pending.length; i += FOOD_GALLERY_LOAD_BATCH) {
            const batch = pending.slice(i, i + FOOD_GALLERY_LOAD_BATCH);
            await Promise.all(
                batch.map(
                    (img) =>
                        new Promise((resolve) => {
                            const finish = () => {
                                img.dataset.loaded = '1';
                                resolve();
                            };
                            const src = img.dataset.src;
                            if (!src) {
                                finish();
                                return;
                            }
                            img.loading = 'lazy';
                            img.decoding = 'async';
                            img.onload = finish;
                            img.onerror = () => {
                                retryImageLoad(img);
                                finish();
                            };
                            img.src = src;
                        })
                )
            );
            if (i + FOOD_GALLERY_LOAD_BATCH < pending.length) {
                await new Promise((r) => window.setTimeout(r, FOOD_GALLERY_BATCH_DELAY_MS));
            }
        }
    } finally {
        container._foodGalleryLoading = false;
    }
}
